import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { isPlatformAdmin } from '../../../utils/platformRole';
import {
  adminListEngagements, adminGetEngagement, adminTimeline, adminSaveTerms, adminIssue, adminReissue,
  adminCountersignAll, adminActivate, adminSuspend, adminResume, adminComplete, adminCancel, adminNote,
  adminDocumentBlob, adminCertificateBlob, adminIntegrity, openPdfBlob, downloadBlob, fmtDateTime,
  STATUS_LABELS, apiError,
} from '../../Engagements/engagementsApi';
import SignaturePad from '../../Engagements/SignaturePad';
import '../Support/SupportConsole.css';
import '../../Engagements/Engagements.css';

const STATUSES = ['REQUESTED', 'ISSUED', 'AWAITING_COUNTERSIGN', 'ACTIVE', 'ON_HOLD', 'SUSPENDED', 'COMPLETED', 'CANCELLED'];
const SERVICES = ['VAPT', 'PENETRATION_TEST', 'BAS', 'ASM', 'RED_TEAM'];

// [key, label, type, required]; required keys mirror the server list.
const TERM_FIELDS = [
  ['effectiveDate', 'Effective date (defaults to the issue date)', 'date', false],
  ['startDate', 'Test start date', 'date', true], ['endDate', 'Test end date', 'date', true], ['reportDate', 'Report delivery date', 'date', true],
  ['retestWindowDays', 'Retest window (days, default 30)', 'number', false],
  ['feeAmount', 'Fee (amount & currency)', 'text', true], ['paymentSchedule', 'Payment schedule', 'text', true],
  ['invoiceDueDays', 'Invoice due (days, default 15)', 'number', false],
  ['governingLaw', 'Governing law (default Ontario)', 'text', false], ['courts', 'Courts / jurisdiction (default Ontario)', 'text', false],
  ['ndaTermYears', 'NDA term (years, default 3)', 'number', false], ['confidentialityCapNote', 'Confidentiality cap (default 2x the fees)', 'text', false],
  ['insuranceNote', 'Insurance clause (blank = clause omitted)', 'text', false],
  ['sourceIps', 'Cataam tester source IPs', 'text', true],
  ['leadEngineerName', 'Lead test engineer', 'text', true], ['leadEngineerPhone', 'Lead engineer phone', 'text', true], ['leadEngineerEmail', 'Lead engineer email', 'text', true],
  ['engagementLeadName', 'Engagement lead (defaults to the Cataam signatory)', 'text', false],
  ['engagementLeadPhone', 'Engagement lead phone', 'text', true], ['engagementLeadEmail', 'Engagement lead email', 'text', true],
  ['signatoryName', 'Cataam signatory name (default from config)', 'text', false], ['signatoryTitle', 'Cataam signatory title (default from config)', 'text', false],
];
const FULL_WIDTH = ['governingLaw', 'courts', 'insuranceNote', 'paymentSchedule', 'sourceIps'];
const OVERRIDE_FIELDS = [
  ['testingApproach', 'Testing approach'], ['exploitationDepth', 'Exploitation depth'], ['realRecordsAccess', 'Access to real records'],
  ['methodology', 'Methodology'], ['severityScheme', 'Severity scheme'], ['reportAudience', 'Report audience'],
  ['testAccounts', 'Test accounts'], ['wafChanges', 'WAF changes'], ['testingWindows', 'Testing windows'], ['outOfScope', 'Out of scope'],
];

const Pill = ({ s }) => <span className={`eng-pill eng-pill-${String(s).toLowerCase()}`}>{STATUS_LABELS[s] || s}</span>;

/** Staff engagement console: intake, terms, issue, countersign, lifecycle actions, timeline. */
export default function EngagementConsole() {
  const { t } = useTranslation();
  const admin = isPlatformAdmin();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [service, setService] = useState('');
  const [selected, setSelected] = useState(null);
  const [events, setEvents] = useState([]);
  const [terms, setTerms] = useState({});
  const [tab, setTab] = useState('overview');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [integrity, setIntegrity] = useState(null);
  const [csOpen, setCsOpen] = useState(false);
  const [csForm, setCsForm] = useState({ typedName: '', typedTitle: '', consent: false, mode: 'type', image: null });

  const load = useCallback(async (p = 0) => {
    setError('');
    try {
      const data = await adminListEngagements({ q: q || undefined, status: status || undefined, serviceType: service || undefined, page: p, size: 20 });
      setItems(data.items || []);
      setTotalPages(data.totalPages || 0);
      setPage(data.page || 0);
    } catch (e) {
      setError(apiError(e, t('AdminEngagements.loadError', 'Could not load engagements.')));
    }
  }, [q, status, service, t]);

  useEffect(() => { load(0); }, [status, service]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (id) => {
    setError('');
    try {
      const [d, ev] = await Promise.all([adminGetEngagement(id), adminTimeline(id)]);
      setSelected(d);
      setEvents(ev);
      setTerms(d.terms || {});
      setIntegrity(null);
    } catch (e) {
      setError(apiError(e, 'Could not load the engagement.'));
    }
  };

  const act = async (fn, okMsg) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      if (okMsg) Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: okMsg, showConfirmButton: false, timer: 2000 });
      await open(selected.id);
      load(page);
    } catch (e) {
      setError(apiError(e, 'Action failed.'));
    } finally {
      setBusy(false);
    }
  };

  const withNote = async (title, min = 0) => {
    const { value, isConfirmed } = await Swal.fire({ title, input: 'textarea', showCancelButton: true,
      inputValidator: (v) => (min && (!v || v.trim().length < min) ? `At least ${min} characters.` : undefined) });
    return isConfirmed ? (value || '') : null;
  };

  const countersign = () => {
    setCsForm({ typedName: '', typedTitle: 'Founder and CEO', consent: false, mode: 'type', image: null });
    setCsOpen(true);
  };

  const submitCountersign = async () => {
    const body = { typedName: csForm.typedName, typedTitle: csForm.typedTitle, consent: csForm.consent,
      signatureImage: csForm.mode === 'draw' ? csForm.image : null };
    setCsOpen(false);
    await act(() => adminCountersignAll(selected.id, body), 'Countersigned');
  };

  const viewDoc = async (d) => { try { openPdfBlob(await adminDocumentBlob(selected.id, d.id)); } catch (e) { setError('Could not open the PDF.'); } };
  const cert = async (d) => { try { downloadBlob(await adminCertificateBlob(selected.id, d.id), `${selected.reference}-${d.templateCode}-certificate.pdf`); } catch (e) { setError('No certificate yet.'); } };

  if (selected) {
    const s = selected;
    const intake = s.intake || {};
    const lp = s.legalProfile || {};
    const canIssue = ['REQUESTED', 'ON_HOLD'].includes(s.status);
    const missing = s.missingTerms || [];
    const issueBlock = !lp.complete ? 'Client legal profile incomplete' : missing.length ? `Fill the required terms first: ${missing.join(', ')}` : '';
    const toCounter = (s.documents || []).filter((d) => d.status === 'CLIENT_SIGNED').length;
    return (
      <div className="spc-page">
        <button className="spc-back" onClick={() => { setSelected(null); load(page); }}><i className="fas fa-arrow-left" /> {t('AdminEngagements.back', 'All engagements')}</button>
        {error && <p className="spc-error">{error}</p>}
        <div className="spc-detail-head">
          <div>
            <h2>{s.reference} · {s.serviceLabel}</h2>
            <div className="spc-meta">
              <span><i className="fas fa-building" /> {s.orgName} (org {s.orgId})</span>
              <span><i className="fas fa-user" /> {s.requestedByEmail || '—'}</span>
              <span><i className="fas fa-clock" /> {fmtDateTime(s.requestedAt)}</span>
              <span>{s.requiredSigned}/{s.requiredTotal} executed</span>
            </div>
          </div>
          <div className="spc-status-ctl"><Pill s={s.status} /></div>
        </div>

        <div className="eng-doc-actions" style={{ margin: '10px 0 14px' }}>
          {canIssue && <button className="eng-btn-primary eng-btn-sm" disabled={busy || !!issueBlock} title={issueBlock} onClick={() => act(() => adminIssue(s.id), 'Documents issued')}><i className="fas fa-paper-plane" /> {s.status === 'ON_HOLD' ? 'Re-issue documents' : 'Issue documents'}</button>}
          {canIssue && issueBlock && <span className="eng-help" style={{ alignSelf: 'center' }}><i className="fas fa-info-circle" /> {issueBlock}{missing.length ? ' (Terms tab)' : ''}</span>}
          {toCounter > 0 && <button className="eng-btn-primary eng-btn-sm" disabled={busy} onClick={countersign}><i className="fas fa-signature" /> Countersign {toCounter} document(s)</button>}
          {s.status === 'ACTIVE' && <button className="eng-btn eng-btn-sm" disabled={busy} onClick={async () => { const n = await withNote('Suspend: reason'); if (n !== null) act(() => adminSuspend(s.id, n), 'Suspended'); }}>Suspend</button>}
          {s.status === 'SUSPENDED' && <button className="eng-btn eng-btn-sm" disabled={busy} onClick={async () => { const n = await withNote('Resume: note'); if (n !== null) act(() => adminResume(s.id, n), 'Resumed'); }}>Resume</button>}
          {['ACTIVE', 'SUSPENDED'].includes(s.status) && <button className="eng-btn eng-btn-sm" disabled={busy} onClick={async () => { const n = await withNote('Complete: closing note'); if (n !== null) act(() => adminComplete(s.id, n), 'Completed'); }}>Complete</button>}
          {admin && !['ACTIVE', 'COMPLETED', 'CANCELLED'].includes(s.status) && <button className="eng-btn eng-btn-sm" disabled={busy} onClick={async () => { const n = await withNote('Activate WITHOUT all signatures — justification (recorded in the audit trail)', 10); if (n !== null) act(() => adminActivate(s.id, n), 'Activated (waived)'); }}><i className="fas fa-exclamation-triangle" /> Waive &amp; activate</button>}
          {!['COMPLETED', 'CANCELLED'].includes(s.status) && <button className="eng-btn-danger eng-btn-sm" disabled={busy} onClick={async () => { const n = await withNote('Cancel: reason'); if (n !== null) act(() => adminCancel(s.id, n), 'Cancelled'); }}>Cancel</button>}
          <button className="eng-btn eng-btn-sm" disabled={busy} onClick={async () => { const n = await withNote('Internal note (visible to the client in the timeline)', 2); if (n) act(() => adminNote(s.id, n)); }}>Add note</button>
          <button className="eng-btn eng-btn-sm" onClick={async () => { try { setIntegrity(await adminIntegrity(s.orgId)); } catch (e) { setError('Integrity check failed.'); } }}><i className="fas fa-link" /> Verify chain</button>
        </div>
        {integrity && <p className={integrity.chainOk ? 'eng-ok' : 'eng-error'}>Signature chain for org {integrity.orgId}: {integrity.signatures} record(s), {integrity.chainOk ? 'intact' : `${integrity.breaks.length} break(s): ${JSON.stringify(integrity.breaks)}`}</p>}

        <div className="eng-tabs">
          {['overview', 'terms', 'documents', 'timeline'].map((k) => <button key={k} className={`eng-tab ${tab === k ? 'eng-tab-on' : ''}`} onClick={() => setTab(k)}>{k}</button>)}
        </div>

        {csOpen && (
          <div className="eng-overlay" onClick={() => !busy && setCsOpen(false)}>
            <div className="eng-modal eng-modal-sm" onClick={(ev) => ev.stopPropagation()}>
              <h3>{t('AdminEngagements.countersignTitle', 'Countersign for Cataam')}</h3>
              <div className="eng-sub">{toCounter} document(s) · {s.reference} · {s.orgName}</div>
              <div className="eng-form" style={{ marginTop: 14 }}>
                <div className="eng-field full"><label>Type your full name *</label>
                  <input value={csForm.typedName} onChange={(ev) => setCsForm({ ...csForm, typedName: ev.target.value })} autoFocus /></div>
                <div className="eng-field full"><label>Your title</label>
                  <input value={csForm.typedTitle} onChange={(ev) => setCsForm({ ...csForm, typedTitle: ev.target.value })} /></div>
              </div>
              <div className="eng-mode">
                <button type="button" className={csForm.mode === 'type' ? 'on' : ''} onClick={() => setCsForm({ ...csForm, mode: 'type' })}><i className="fas fa-keyboard" /> Type</button>
                <button type="button" className={csForm.mode === 'draw' ? 'on' : ''} onClick={() => setCsForm({ ...csForm, mode: 'draw' })}><i className="fas fa-pen-fancy" /> Draw</button>
              </div>
              {csForm.mode === 'type'
                ? <div className="eng-typed-preview">{csForm.typedName || '\u00a0'}</div>
                : <SignaturePad onChange={(img) => setCsForm((f) => ({ ...f, image: img }))} />}
              <label className="eng-consent">
                <input type="checkbox" checked={csForm.consent} onChange={(ev) => setCsForm({ ...csForm, consent: ev.target.checked })} />
                <span>{s.cataamConsentText}</span>
              </label>
              <div className="eng-modal-foot">
                <button className="eng-btn" onClick={() => setCsOpen(false)}>Cancel</button>
                <button className="eng-btn-primary" onClick={submitCountersign}
                  disabled={busy || !csForm.consent || csForm.typedName.trim().length < 2 || (csForm.mode === 'draw' && !csForm.image)}>
                  <i className="fas fa-signature" /> Countersign {toCounter} document(s)
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === 'overview' && (
          <div className="eng-two-col">
            <div className="eng-panel">
              <h3>Client legal profile {lp.complete ? <span className="eng-pill eng-pill-active">complete</span> : <span className="eng-pill eng-pill-on_hold">incomplete</span>}</h3>
              <dl className="eng-kv">
                {['legalName', 'tradingName', 'entityType', 'registrationNumber', 'registeredAddress', 'jurisdiction', 'signatoryName', 'signatoryTitle', 'signatoryEmail'].map((k) => <React.Fragment key={k}><dt>{k}</dt><dd>{lp[k] || '—'}</dd></React.Fragment>)}
              </dl>
              <h3 style={{ marginTop: 14 }}>Contacts</h3>
              {(intake.contacts || []).map((c, i) => <div key={i} style={{ fontSize: '0.82rem' }}>{c.role}: <strong>{c.name}</strong> · {c.phone} · {c.email}</div>)}
              <h3 style={{ marginTop: 14 }}>Scope ({(intake.assets || []).length})</h3>
              <table className="eng-table"><tbody>{(intake.assets || []).map((a, i) => <tr key={i}><td>{i + 1}</td><td>{a.asset}</td><td>{a.reference}</td><td>{a.env}</td><td>{a.stack}</td></tr>)}</tbody></table>
              <h3 style={{ marginTop: 14 }}>Providers</h3>
              {(intake.providers || []).map((p, i) => <div key={i} style={{ fontSize: '0.82rem' }}>{p.name}: {p.services} · approval {p.approvalNeeded} {p.reference}</div>)}
            </div>
            <div className="eng-panel">
              <h3>Intake answers (open items)</h3>
              <dl className="eng-kv">
                {['environment', 'hasRealData', 'backupNote', 'testingApproach', 'exploitationDepth', 'realRecordsAccess', 'methodology', 'severityScheme', 'reportAudience', 'testingWindows', 'fragileComponents', 'testAccounts', 'wafChanges', 'outOfScope', 'sourceCodeRepo', 'preferredStartDate', 'notes']
                  .map((k) => <React.Fragment key={k}><dt>{k}</dt><dd>{intake[k] === undefined || intake[k] === '' ? '—' : String(intake[k])}</dd></React.Fragment>)}
              </dl>
              {s.activationSource && <p className="eng-help" style={{ marginTop: 10 }}>Activation: {s.activationSource} · {fmtDateTime(s.activatedAt)} · {s.activationNote}</p>}
            </div>
          </div>
        )}

        {tab === 'terms' && (
          <div className="eng-panel">
            <h3>Cataam terms (merged into the agreements at issue) · <span style={{ color: '#b91c1c' }}>*</span> required before issue</h3>
            {missing.length > 0 && <p className="eng-error"><i className="fas fa-exclamation-circle" /> Still missing: {missing.join(', ')}</p>}
            {missing.length === 0 && <p className="eng-ok"><i className="fas fa-check-circle" /> All required terms are set.</p>}
            <div className="eng-form">
              {TERM_FIELDS.map(([k, label, type, required]) => (
                <div key={k} className={`eng-field ${FULL_WIDTH.includes(k) ? 'full' : ''}`}>
                  <label>{label}{required && <span style={{ color: '#b91c1c' }}> *</span>}</label>
                  <input type={type} value={terms[k] || ''} onChange={(e) => setTerms({ ...terms, [k]: e.target.value })}
                    style={required && !(terms[k] || '').trim() ? { borderColor: '#fca5a5' } : undefined} />
                </div>
              ))}
            </div>
            <div className="eng-help" style={{ marginTop: 6 }}>Dates are printed in the agreements as words, e.g. "10 January 2027".</div>
            <h3 style={{ marginTop: 16 }}>Overrides of the client's answers (Schedule 2 / ATT)</h3>
            <div className="eng-help" style={{ marginBottom: 8 }}>Leave blank to keep what the client entered.</div>
            <div className="eng-form">
              {OVERRIDE_FIELDS.map(([k, label]) => (
                <div key={k} className="eng-field">
                  <label>{label} <span className="eng-help" style={{ display: 'inline' }}>(client: {intake[k] || '—'})</span></label>
                  <input value={terms[k] || ''} onChange={(e) => setTerms({ ...terms, [k]: e.target.value })} />
                </div>
              ))}
            </div>
            <div className="eng-modal-foot">
              <span className="eng-help">Terms can be edited until the engagement is active.</span>
              <button className="eng-btn-primary" disabled={busy || ['ACTIVE', 'COMPLETED', 'CANCELLED'].includes(s.status)} onClick={() => act(() => adminSaveTerms(s.id, terms), 'Terms saved (draft)')}>Save terms</button>
            </div>
          </div>
        )}

        {tab === 'documents' && (
          <div className="eng-panel">
            <table className="eng-table">
              <thead><tr><th>Document</th><th>Status</th><th>Signatures</th><th /></tr></thead>
              <tbody>
                {(s.documents || []).map((d) => (
                  <tr key={d.id} style={d.status === 'SUPERSEDED' ? { opacity: 0.55 } : undefined}>
                    <td><strong>{d.templateName}</strong><div className="eng-help">{d.templateCode} v{d.versionNo} · doc {d.id}</div><div className="eng-mono">{d.renderedSha256}</div></td>
                    <td><span className={`eng-pill eng-pill-${String(d.status).toLowerCase()}`}>{d.status}</span><div className="eng-help">viewed {fmtDateTime(d.firstViewedAt)}</div></td>
                    <td>{(d.signatures || []).map((sg) => (
                      <div key={sg.id} style={{ fontSize: '0.76rem', marginBottom: 4 }}>
                        <strong>{sg.party}</strong> {sg.action} · {sg.typedName}{sg.typedTitle ? `, ${sg.typedTitle}` : ''} · {sg.signerEmail}<br />
                        <span className="eng-help">{fmtDateTime(sg.signedAt)} UTC · {sg.ipAddress} · {sg.signerAuthSource}/{sg.signerMfaEnabled ? 'MFA' : 'no MFA'}</span><br />
                        <span className="eng-mono">{sg.fingerprint}</span>
                        {sg.declineReason && <div className="eng-error">Reason: {sg.declineReason}</div>}
                      </div>
                    ))}</td>
                    <td>
                      <div className="eng-doc-actions">
                        <button className="eng-btn eng-btn-sm" onClick={() => viewDoc(d)}><i className="fas fa-eye" /> PDF</button>
                        {(d.signatures || []).length > 0 && <button className="eng-btn eng-btn-sm" onClick={() => cert(d)}><i className="fas fa-certificate" /> Certificate</button>}
                        {d.status !== 'SUPERSEDED' && <button className="eng-btn eng-btn-sm" disabled={busy} onClick={async () => { const n = await withNote('Re-issue this document (supersedes the current one): note'); if (n !== null) act(() => adminReissue(s.id, d.id, n), 'Re-issued'); }}>Re-issue</button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {(s.documents || []).length === 0 && <tr><td colSpan={4} className="spc-empty">No documents issued yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'timeline' && (
          <div className="eng-panel">
            <ul className="eng-timeline">
              {events.map((ev) => (
                <li key={ev.id}>
                  <div className="when">{fmtDateTime(ev.occurredAt)} UTC</div>
                  <div>
                    <div className="what">{ev.eventType}{ev.fromStatus || ev.toStatus ? ` (${ev.fromStatus || '—'} → ${ev.toStatus || '—'})` : ''}{ev.documentId ? ` · doc ${ev.documentId}` : ''}{ev.details ? ` — ${ev.details}` : ''}</div>
                    <div className="who">{ev.actorType}{ev.actorEmail ? ` · ${ev.actorEmail}` : ''}{ev.peeking ? ' · PEEK' : ''}{ev.ipAddress ? ` · ${ev.ipAddress}` : ''}{ev.userAgent ? ` · ${ev.userAgent.slice(0, 60)}` : ''}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="spc-page">
      {error && <p className="spc-error">{error}</p>}
      <div className="spc-toolbar">
        <div className="spc-search">
          <i className="fas fa-search" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load(0)} placeholder={t('AdminEngagements.searchPh', 'Search reference, organisation, requester…')} />
          <button className="spc-btn-primary" onClick={() => load(0)}>{t('SupportConsole.search', 'Search')}</button>
          <select value={service} onChange={(e) => setService(e.target.value)} style={{ border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 8px', fontSize: '0.8rem' }}>
            <option value="">All services</option>
            {SERVICES.map((sv) => <option key={sv} value={sv}>{sv}</option>)}
          </select>
        </div>
        <div className="spc-filters">
          <button className={!status ? 'spc-chip spc-chip-on' : 'spc-chip'} onClick={() => setStatus('')}>All</button>
          {STATUSES.map((st) => <button key={st} className={status === st ? 'spc-chip spc-chip-on' : 'spc-chip'} onClick={() => setStatus(st)}>{st}</button>)}
        </div>
      </div>
      <table className="spc-table">
        <thead><tr><th>Reference</th><th>Status</th><th>Service</th><th>Organisation</th><th>Requested by</th><th>Executed</th><th>Updated</th></tr></thead>
        <tbody>
          {items.map((e) => (
            <tr key={e.id} onClick={() => open(e.id)}>
              <td className="spc-ticketno">{e.reference}</td>
              <td><Pill s={e.status} /></td>
              <td>{e.serviceLabel}</td>
              <td>{e.orgName || e.orgId}</td>
              <td>{e.requestedByEmail || '—'}</td>
              <td>{e.requiredSigned}/{e.requiredTotal}{e.pendingCountersignatures > 0 ? ` · ${e.pendingCountersignatures} to countersign` : ''}</td>
              <td>{fmtDateTime(e.updatedAt)}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={7} className="spc-empty">{t('AdminEngagements.empty', 'No engagements found.')}</td></tr>}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className="spc-pager">
          <button disabled={page === 0} onClick={() => load(page - 1)}>&larr;</button>
          <span>{page + 1} / {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => load(page + 1)}>&rarr;</button>
        </div>
      )}
    </div>
  );
}
